import { useCallback, useMemo } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  type Node,
  type Edge,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AgentNode, type AgentNodeData } from './AgentNode';
import type { AgentInfo } from '@/types';

const nodeTypes = {
  agent: AgentNode,
};

// 에이전트 간의 논리적 관계 정의
const AGENT_RELATIONSHIPS: Array<{
  source: string;
  target: string;
  label?: string;
  type: 'delegates' | 'reviews' | 'supports';
}> = [
  // Orchestrator가 최상위에서 다른 에이전트들에게 작업 위임
  { source: 'orchestrator', target: 'oracle', type: 'delegates', label: '분석 위임' },
  { source: 'orchestrator', target: 'planning', type: 'delegates', label: '계획 위임' },
  { source: 'orchestrator', target: 'search', type: 'delegates', label: '검색 위임' },

  // Oracle이 Reviewer에게 검토 요청
  { source: 'oracle', target: 'reviewer', type: 'reviews', label: '검토 요청' },

  // Planning이 Frontend/Backend에게 구현 위임
  { source: 'planning', target: 'frontend', type: 'delegates', label: 'UI 설계' },
  { source: 'planning', target: 'backend', type: 'delegates', label: 'API 설계' },

  // Frontend/Backend가 Mocking을 활용
  { source: 'frontend', target: 'mocking', type: 'supports', label: '프로토타입' },
  { source: 'backend', target: 'mocking', type: 'supports', label: '목 데이터' },

  // Explorer가 코드베이스 탐색 지원
  { source: 'search', target: 'explorer', type: 'delegates', label: '코드 탐색' },
];

// 노드 위치 계산 (계층적 레이아웃) - 넓은 간격
const NODE_POSITIONS: Record<string, { x: number; y: number }> = {
  orchestrator: { x: 450, y: 0 },
  oracle: { x: 100, y: 180 },
  planning: { x: 450, y: 180 },
  search: { x: 800, y: 180 },
  reviewer: { x: 100, y: 380 },
  frontend: { x: 300, y: 380 },
  backend: { x: 600, y: 380 },
  explorer: { x: 800, y: 380 },
  mocking: { x: 450, y: 580 },
  general_chat: { x: 950, y: 0 },
};

const edgeStyles: Record<string, { stroke: string; strokeDasharray?: string }> = {
  delegates: { stroke: '#3b82f6' }, // blue
  reviews: { stroke: '#f59e0b', strokeDasharray: '5,5' }, // amber, dashed
  supports: { stroke: '#10b981', strokeDasharray: '3,3' }, // green, dotted
};

interface AgentTopologyGraphProps {
  agents: AgentInfo[];
  onAgentClick?: (agent: AgentInfo) => void;
}

export function AgentTopologyGraph({ agents, onAgentClick }: AgentTopologyGraphProps) {
  // 에이전트 정보를 타입별로 매핑
  const agentMap = useMemo(() => {
    const map = new Map<string, AgentInfo>();
    agents.forEach((agent) => {
      map.set(agent.type, agent);
    });
    return map;
  }, [agents]);

  // 노드 클릭 핸들러
  const handleNodeClick = useCallback(
    (agentType: string) => {
      const agent = agentMap.get(agentType);
      if (agent && onAgentClick) {
        onAgentClick(agent);
      }
    },
    [agentMap, onAgentClick]
  );

  // 노드 생성
  const nodes: Node[] = useMemo(() => {
    return agents
      .filter((agent) => NODE_POSITIONS[agent.type]) // 위치가 정의된 에이전트만
      .map((agent) => {
        const nodeData: AgentNodeData = {
          type: agent.type,
          name: agent.name,
          nameKo: agent.nameKo,
          modelTier: agent.modelTier,
          costTier: agent.costTier,
          onClick: () => handleNodeClick(agent.type),
        };
        return {
          id: agent.type,
          type: 'agent',
          position: NODE_POSITIONS[agent.type] || { x: 0, y: 0 },
          data: nodeData as unknown as Record<string, unknown>,
        };
      });
  }, [agents, handleNodeClick]);

  // 엣지 생성
  const edges: Edge[] = useMemo(() => {
    return AGENT_RELATIONSHIPS.filter(
      (rel) => agentMap.has(rel.source) && agentMap.has(rel.target)
    ).map((rel, index) => ({
      id: `e-${rel.source}-${rel.target}-${index}`,
      source: rel.source,
      target: rel.target,
      label: rel.label,
      labelStyle: { fontSize: 10, fill: '#64748b' },
      labelBgStyle: { fill: 'white', fillOpacity: 0.8 },
      labelBgPadding: [4, 2] as [number, number],
      style: edgeStyles[rel.type],
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 15,
        height: 15,
        color: edgeStyles[rel.type].stroke,
      },
      animated: rel.type === 'delegates',
    }));
  }, [agentMap]);

  return (
    <div className="h-[650px] w-full border rounded-lg bg-background relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        <Controls showInteractive={false} />
        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="!bg-muted/50"
        />
      </ReactFlow>

      {/* 범례 */}
      <div className="absolute bottom-4 left-4 p-3 bg-background/90 backdrop-blur-sm border rounded-lg text-xs space-y-2">
        <div className="font-semibold text-muted-foreground">관계 범례</div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-blue-500" />
          <span>작업 위임 (delegates)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5" style={{ background: 'repeating-linear-gradient(90deg, #f59e0b 0, #f59e0b 3px, transparent 3px, transparent 6px)' }} />
          <span>검토 요청 (reviews)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5" style={{ background: 'repeating-linear-gradient(90deg, #10b981 0, #10b981 2px, transparent 2px, transparent 4px)' }} />
          <span>지원 (supports)</span>
        </div>
      </div>
    </div>
  );
}
